import { v4 as uuidv4 } from "uuid";
import { FilterValues } from "../../../data-model/filterValues";
import { buildAsteraBiosimilarFilterTree } from "./buildAsteraBiosimilarFilterTree";
import {
  AsteraOncologyExternalFilterPayload,
  ASTERA_ONCOLOGY_EXTERNAL_DEFAULT_ORDER_BY,
} from "./asteraOncologyExternalFilterPayload";
import {
  buildSearchFilterTree,
  convertLegacyFiltersToConditions,
  FilterGroup,
  mergeSearchFiltersWithSidebarFilters,
} from "./buildAsteraSearchFilters";
import {
  convertDefaultDateToDumpedAtFormat,
  processFilterData,
} from "./processFilterData";

/**
 * Combines flat sidebar filters with the Biosimilar predicate tree when selected.
 *
 * @param apiFilters - Flat filter map from processFilterData
 * @param biosimilarStates - Selected Biosimilar filter values
 * @return FilterGroup | null: Combined sidebar predicate tree, or null when Biosimilar is not active
 */
const buildSidebarFilterTree = (
  apiFilters: Record<string, string[] | null>,
  biosimilarStates: string[],
): FilterGroup | null => {
  const biosimilarTree = buildAsteraBiosimilarFilterTree(biosimilarStates);
  if (!biosimilarTree) {
    return null;
  }

  const sidebarConditions = convertLegacyFiltersToConditions(apiFilters);
  const children = [...sidebarConditions, biosimilarTree];

  if (children.length === 1) {
    return children[0] as FilterGroup;
  }

  return {
    type: "group",
    operator: "and",
    children,
  };
};

/**
 * Builds the Astera oncology external table API payload for filter and search requests.
 *
 * @param validFilters - Active sidebar filters for this table
 * @param searchTerm - Current search text (empty when not searching)
 * @param dateFromStart - Default range start date (YYYYMMDD)
 * @param dateFromEnd - Default range end date (YYYYMMDD)
 * @param pageSize - Number of rows per page
 * @param page - Page number to request (1-indexed)
 * @returns AsteraOncologyExternalFilterPayload: Request body for the BigQuery API
 */
export const buildAsteraFilterPayload = (
  validFilters: FilterValues[],
  searchTerm: string,
  dateFromStart: string,
  dateFromEnd: string,
  pageSize: number,
  page: number = 1,
): AsteraOncologyExternalFilterPayload => {
  const { filters: apiFilters, biosimilarStates } =
    processFilterData(validFilters);

  const hasSearchTerm = searchTerm.trim().length > 0;
  const hasFilters = validFilters.length > 0;

  const omitDates = hasSearchTerm && !hasFilters;
  if (!omitDates) {
    if (!apiFilters.dumped_at || apiFilters.dumped_at.length < 2) {
      apiFilters.dumped_at = [
        convertDefaultDateToDumpedAtFormat(dateFromStart),
        convertDefaultDateToDumpedAtFormat(dateFromEnd),
      ];
    }
  }

  const sidebarFilterTree = buildSidebarFilterTree(
    apiFilters,
    biosimilarStates,
  );

  if (hasSearchTerm) {
    const searchTree = buildSearchFilterTree(searchTerm);

    return {
      case_sensitive: false,
      filters: sidebarFilterTree
        ? {
            type: "group",
            operator: "and",
            children: [sidebarFilterTree, searchTree],
          }
        : mergeSearchFiltersWithSidebarFilters(searchTerm, apiFilters),
      page,
      page_size: pageSize,
      request_id: uuidv4(),
      order_by: ASTERA_ONCOLOGY_EXTERNAL_DEFAULT_ORDER_BY,
    };
  }

  return {
    filters: sidebarFilterTree ?? apiFilters,
    page,
    page_size: pageSize,
    request_id: uuidv4(),
    order_by: ASTERA_ONCOLOGY_EXTERNAL_DEFAULT_ORDER_BY,
  };
};
